import type { SVGAttributes } from 'react';
import { PortIconSVG } from 'atto-svgs';
import React from 'react';

import type {
  PortIndicator,
  PortIndicatorOptions,
  PortIndicatorPlacement,
  PortPort,
  PortSpeed,
  PortStatus,
  PortVariant,
} from './utils';
import { Icon } from '../../assets/Icon/Icon';
import { FocusRing } from '../../common/focus_rings';
import { colors, darkThemeSelector, styled } from '../../stitches.config';
import { Text } from '../../text/Text';
import { getPortAccessibleName, getPortIcon, getPortSpeedLabel, getPortVariant } from './utils';

export { type PortIndicator, type PortIndicatorOptions, type PortIndicatorPlacement };
export { type PortPort, type PortSpeed, type PortStatus, type PortVariant };
export { getPortIndicators, getPortSpeedLabel, getPortVariant } from './utils';

const PortSVGs = {
  ethernet: PortIconSVG.Ethernet,
  sfp: PortIconSVG.SFP,
};

const portValues = {
  opacity: {
    disabled: 0.85,
    /**
     * How far the glyph and its speed drop back behind the ✕.
     *
     * The mark is drawn in the same colour as the art it crosses, so the art has to give way for it
     * to read at all. A second copy of the asset used to be laid over the top at 60% instead, which
     * added ink where it meant to remove it — a disabled port came out darker than a connected one,
     * with the ✕ lost somewhere inside it.
     */
    disabledGlyph: 0.3,
  },
  zIndex: {
    representation: 0,
    contents: 1,
    blocking: 2,
    disabled: 2,
    stamp: 3,
  },
};

/**
 * The glyph box per mode, all on the asset's 6:5 ratio.
 *
 * `PortIconSVG` is authored at 24×20 with a 1-unit border, so `detailed` draws it 1:1 and that border
 * lands on a whole pixel. The other two are exact fractions of the same ratio (0.75× and 0.5×) — an
 * off-ratio box doesn't crop or stretch, it letterboxes, because the SVG keeps its aspect and centres
 * itself. The previous boxes (32×29, 20×18, 12×11) were all ratios of the old 32×28 Ethernet, and the
 * old SFP at 40×36 was squashed into them.
 */
const portSVGSizing = {
  simple: {
    width: '$18',
    height: '$15',
  },
  icon: {
    width: '$12',
    height: '$10',
  },
  detailed: {
    width: '$24',
    height: '$20',
  },
};

const PortSVG = styled('svg', {
  position: 'absolute',
  top: 0,
  left: 0,
  display: 'flex',
  overflow: 'visible',
  /*
   * One colour, three opacities — the asset paints its own border as a solid 1-unit ring, its body at
   * 25%, and (on Ethernet) its pins at 50%, all `currentColor`. So this is the saturated stroke token
   * rather than the pale background one: at `hardwareBg*` the ring would come out `green100` and all
   * but disappear on a white card, and the body behind it would be a quarter of that.
   *
   * `stroke`/`strokeWidth`/`paintOrder` used to sit here because the old assets were one flat shape
   * with no border of their own. A stroke now traces every layer the asset already draws — both edges
   * of the ring, and each 1-unit pin at 2 units wide, which merges the eight of them into a bar.
   */
  color: '$$portStrokeColor',

  variants: {
    orientation: {
      up: {},
      down: {
        transform: 'rotate(180deg)',
      },
    },
    mode: {
      detailed: {
        width: portSVGSizing.detailed.width,
        height: portSVGSizing.detailed.height,
      },
      icon: {
        widthAll: portSVGSizing.icon.width,
        heightAll: portSVGSizing.icon.height,
      },
      simple: {
        width: portSVGSizing.simple.width,
        height: portSVGSizing.simple.height,
      },
    },
  },
});
PortSVG.displayName = 'PortSVG';

const PortRepresentation = styled('div', {
  position: 'absolute',
  zIndex: portValues.zIndex.representation,
  top: 0,
  left: 0,
  display: 'flex',

  variants: {
    /** Fades the asset so the ✕ over it has something to read against. */
    disabled: {
      true: {
        opacity: portValues.opacity.disabledGlyph,
      },
      false: {},
    },
  },
});
PortRepresentation.displayName = 'PortRepresentation';

const PortIcon = styled(Icon, {
  color: '$$portContentsColor',

  variants: {
    disabled: {
      true: {
        opacity: portValues.opacity.disabled,
      },
      false: {},
    },
  },
});
PortIcon.displayName = 'PortIcon';

const PortCopy = styled(Text, {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  fontFamily: '$mono',
  fontSize: '$10',
  lineHeight: '$12',

  variants: {
    mode: {
      detailed: {
        heightAll: '$12',
        /*
         * A step down from the `$10` simple mode keeps, because the glyph narrowed from 32 to 24 with
         * the redrawn art. The widest labels ('2.5G', '100M') ink 23.3px at `$10` — wider than the
         * 22px inside the asset's 1-unit ring, so the text came to rest on the border itself. At `$9`
         * they ink 21px and clear it, and `PortSpeedPoint` takes another 2px off the fractional ones.
         * `$9` is the floor of the scale, and the size `PortStamp` next to it already uses.
         */
        fontSize: '$9',
      },
      icon: {},
      simple: {
        widthAll: portSVGSizing.simple.width,
        heightAll: portSVGSizing.simple.height,
      },
    },
    orientation: {
      up: {},
      down: {},
    },
  },

  compoundVariants: [
    {
      mode: 'simple',
      orientation: 'down',
      css: {
        paddingBottom: '$2',
      },
    },
    {
      mode: 'simple',
      orientation: 'up',
      css: {
        paddingTop: '$2',
      },
    },
  ],
});
PortCopy.displayName = 'PortCopy';

/**
 * The point in a speed, closed up on both sides.
 *
 * In a monospace face a point takes a full advance of almost entirely whitespace, and the label has
 * only the 22px inside the asset's 1-unit ring to live in. Taking a pixel off each side is what buys
 * '2.5G' room to sit inside that ring rather than resting on it, and only the fractional speeds pay
 * for the character at all — no other label on a faceplate is touched.
 *
 * `letterSpacing` closes the gap after the point; the negative margin closes the one before it,
 * which letter-spacing on a single character can't reach.
 */
const PortSpeedPoint = styled('span', {
  marginLeft: '-1px',
  letterSpacing: '-1px',
});
PortSpeedPoint.displayName = 'PortSpeedPoint';

/** A port's rated speed — '2.5G', '10G', '100M' — set to fit the glyph. See `PortSpeedPoint`. */
function PortSpeedLabel({ speed }: { speed: PortSpeed }) {
  const [whole, fraction] = getPortSpeedLabel(speed).split('.');

  if (fraction === undefined) return whole;

  return (
    <>
      {whole}
      <PortSpeedPoint>.</PortSpeedPoint>
      {fraction}
    </>
  );
}

const PortContents = styled('div', {
  position: 'relative',
  zIndex: portValues.zIndex.contents,
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  height: '100%',
  color: '$$portContentsColor',

  variants: {
    /**
     * Fades the speed with the asset behind it, for the same reason.
     *
     * Simple mode never sets this: the ✕ can't be drawn there, so the number in its place is the
     * only thing distinguishing one dead port from another and has to stay legible.
     */
    disabled: {
      true: {
        opacity: portValues.opacity.disabledGlyph,
      },
      false: {},
    },
    orientation: {
      up: {
        flexDirection: 'column',
        justifyContent: 'end',
      },
      down: {
        flexDirection: 'column-reverse',
        justifyContent: 'start',
      },
    },
    mode: {
      detailed: {},
      icon: {},
      simple: {},
    },
  },
  compoundVariants: [
    /*
     * The label sits at the cable end, over the pins the redrawn Ethernet asset draws there. That
     * overlap is what the design calls for, so it isn't nudged away from them — the pins are a 50%
     * layer and the label rides on top of it.
     */
    {
      mode: 'detailed',
      orientation: 'up',
      css: {
        paddingBottom: '$2',
      },
    },
    {
      mode: 'detailed',
      orientation: 'down',
      css: {
        paddingTop: '$2',
      },
    },
  ],
});
PortContents.displayName = 'PortContents';

const PortDisabled = styled('div', {
  position: 'absolute',
  zIndex: portValues.zIndex.disabled,
  inset: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});
PortDisabled.displayName = 'PortDisabled';

const PortBlocking = styled('div', {
  position: 'absolute',
  zIndex: portValues.zIndex.blocking,
  inset: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});
PortBlocking.displayName = 'PortBlocking';

const PortContainer = styled('div', FocusRing, {
  position: 'relative',
  display: 'flex',
  alignItems: 'stretch',
  justifyContent: 'stretch',

  variants: {
    // Radii track the asset's 4-unit corner through each mode's scale, so the focus ring and the
    // selection fill follow the glyph's outline rather than a rounder box around it.
    mode: {
      detailed: {
        widthAll: portSVGSizing.detailed.width,
        heightAll: portSVGSizing.detailed.height,
        borderRadius: '$4',
      },
      icon: {
        widthAll: portSVGSizing.icon.width,
        heightAll: portSVGSizing.icon.height,
        borderRadius: '$2',
      },
      simple: {
        widthAll: portSVGSizing.simple.width,
        heightAll: portSVGSizing.simple.height,
        borderRadius: '$3',
      },
    },
    selected: {
      true: {},
      false: {},
    },
    /*
     * No `$$portBgColor`: the asset derives its own fill from `$$portStrokeColor` at 25%, so the
     * `hardwareBg*` family no longer reaches a port and the stroke carries a port's whole meaning.
     *
     * `$$portStampColor` outlived the `PortStamp` it was named for; nothing reads it now.
     */
    variant: {
      alternative: {
        $$portContentsColor: colors.hardwareContentAlternativeLight,
        $$portStrokeColor: colors.hardwareStrokeAlternativeLight,
        $$portStampColor: colors.hardwareStampAlternativeLight,

        [darkThemeSelector]: {
          $$portContentsColor: colors.hardwareContentAlternativeDark,
          $$portStrokeColor: colors.hardwareStrokeAlternativeDark,
          $$portStampColor: colors.hardwareStampAlternativeDark,
        },
      },
      attention: {
        $$portContentsColor: colors.hardwareContentAttentionLight,
        $$portStrokeColor: colors.hardwareStrokeAttentionLight,
        $$portStampColor: colors.hardwareStampAttentionLight,

        [darkThemeSelector]: {
          $$portContentsColor: colors.hardwareContentAttentionDark,
          $$portStrokeColor: colors.hardwareStrokeAttentionDark,
          $$portStampColor: colors.hardwareStampAttentionDark,
        },
      },
      brand: {
        $$portContentsColor: colors.hardwareContentBrandLight,
        $$portStrokeColor: colors.hardwareStrokeBrandLight,
        $$portStampColor: colors.hardwareStampBrandLight,

        [darkThemeSelector]: {
          $$portContentsColor: colors.hardwareContentBrandDark,
          $$portStrokeColor: colors.hardwareStrokeBrandDark,
          $$portStampColor: colors.hardwareStampBrandDark,
        },
      },
      disabled: {
        $$portContentsColor: colors.hardwareContentDisabledLight,
        $$portStrokeColor: colors.hardwareStrokeDisabledLight,
        $$portStampColor: colors.hardwareStampDisabledLight,

        [darkThemeSelector]: {
          $$portContentsColor: colors.hardwareContentDisabledDark,
          $$portStrokeColor: colors.hardwareStrokeDisabledDark,
          $$portStampColor: colors.hardwareStampDisabledDark,
        },
      },
      negative: {
        $$portContentsColor: colors.hardwareContentNegativeLight,
        $$portStrokeColor: colors.hardwareStrokeNegativeLight,
        $$portStampColor: colors.hardwareStampNegativeLight,

        [darkThemeSelector]: {
          $$portContentsColor: colors.hardwareContentNegativeDark,
          $$portStrokeColor: colors.hardwareStrokeNegativeDark,
          $$portStampColor: colors.hardwareStampNegativeDark,
        },
      },
      neutral: {
        $$portContentsColor: colors.hardwareContentNeutralLight,
        $$portStrokeColor: colors.hardwareStrokeNeutralLight,
        $$portStampColor: colors.hardwareStampNeutralLight,

        [darkThemeSelector]: {
          $$portContentsColor: colors.hardwareContentNeutralDark,
          $$portStrokeColor: colors.hardwareStrokeNeutralDark,
          $$portStampColor: colors.hardwareStampNeutralDark,
        },
      },
      positive: {
        $$portContentsColor: colors.hardwareContentPositiveLight,
        $$portStrokeColor: colors.hardwareStrokePositiveLight,
        $$portStampColor: colors.hardwareStampPositiveLight,

        [darkThemeSelector]: {
          $$portContentsColor: colors.hardwareContentPositiveDark,
          $$portStrokeColor: colors.hardwareStrokePositiveDark,
          $$portStampColor: colors.hardwareStampPositiveDark,
        },
      },
    },
  },
});
PortContainer.displayName = 'PortContainer';

export type PortIconName = keyof typeof PortSVGs;

export type PortOnClick = (event: any) => void;
export type PortOrientation = 'down' | 'up';
export type PortMode = 'detailed' | 'icon' | 'simple';

/**
 * The glyph alone — shape, speed text, status colour, the ✕ overlay and the SFP stamp.
 *
 * Properties a port advertises (PoE, uplink, blocking, SPAN, layer 3) are drawn outside the glyph
 * from `getPortIndicators` — by `SlotIndicators` on the rail, and by `SlotCaption` for the ones that
 * belong beside the number chip instead. `blocking`/`uplink`/`isL3Port`/
 * `isOspfEnabled` survive here only because simple mode can swap the number for a single icon; `poe`
 * and `spanMirroring` never reached that path, so they live on `ChassisPort` now.
 */
export type PortProps = {
  /**
   * The port is blocking.
   */
  blocking?: boolean;
  /**
   * The port number.
   */
  number?: number;
  /**
   * The function to call when the port is clicked.
   */
  onClick?: PortOnClick;
  /**
   * The orientation of the port, either `up` or `down`.
   */
  orientation?: PortOrientation;
  /**
   * The port type, either `ethernet` or `sfp`.
   */
  port: PortPort;
  /**
   * The port is selected.
   */
  selected?: boolean;
  /**
   * The port max speed in Mbps.
   */
  maximumPortSpeed?: PortSpeed;
  /**
   * Current negotiated speed of the port.
   */
  negotiatedPortSpeed?: PortSpeed;
  /**
   * The port is uplink.
   */
  uplink?: boolean;
  /**
   * Whether the port is disabled, takes precedence over disabled status.
   */
  isDisabled?: boolean;
  /**
   * The port status, either `connected`, `disconnected`, `error`, or `disabled`.
   */
  status?: PortStatus;
  /**
   * The port variant.
   */
  variant?: PortVariant;
  /**
   * The mode of the port, either `detailed`, `icon`, or `simple`.
   */
  mode?: PortMode;
  /**
   * The port is an Out-of-Band port.
   */
  isOutOfBandPort?: boolean;
  /**
   * The port is a high availability port.
   */
  isHAPort?: boolean;
  /**
   * The port is a gateway port.
   */
  isGatewayPort?: boolean;
  /**
   * Show the port icon (e.g. uplink arrow) instead of the port number in simple mode.
   */
  showIconInSimpleMode?: boolean;
  /**
   * Show the L3 status
   */
  isL3Port?: boolean;
  /**
   * Show the OSPF status
   */
  isOspfEnabled?: boolean;
};

export function Port({
  blocking = false,
  number,
  onClick,
  orientation = 'up',
  port,
  maximumPortSpeed,
  negotiatedPortSpeed,
  uplink = false,
  selected = false,
  isDisabled,
  status = 'disconnected',
  variant,
  mode = 'detailed',
  isHAPort,
  isOutOfBandPort,
  // Semantic, not visual — destructured only to keep it off the DOM.
  isGatewayPort,
  showIconInSimpleMode = false,
  isL3Port = false,
  isOspfEnabled = false,
  ...remaining
}: PortProps) {
  const NamedIcon = PortSVGs[port as PortIconName] as
    | React.ComponentType<SVGAttributes<SVGSVGElement>>
    | undefined;

  const isIcon = mode === 'icon';
  const isSimplified = mode === 'simple' || isIcon;

  const disabled = isDisabled || status === 'disabled';
  // Simple mode has room for exactly one icon in place of the number, so it keeps the old priority
  // chain. Detailed mode shows all of them, on the slot's indicator rail.
  const portIcon = getPortIcon(uplink, blocking, false, isL3Port, isOspfEnabled);
  /*
   * The ✕ is drawn across the glyph's face — which in simple mode is where the number sits, so that
   * is the one mode that can't carry the mark. Without it, the flatter `disabled` grey is all that
   * is left to say the port is off.
   */
  const marksDisabled = disabled && mode !== 'simple';
  // Colour follows the negotiated link, never the rated one, so a port passing no traffic can't
  // borrow the green of one that is. Its rating still reads off the speed label.
  const portVariant =
    variant ??
    (disabled && !marksDisabled ? 'disabled' : getPortVariant(negotiatedPortSpeed, status));
  const showSpeed = !isSimplified;

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (onClick && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      onClick(event);
    }
  };

  return (
    /*
     * Button, focus target and click target are one element on purpose.
     *
     * They used to be two: the container took the `tabIndex` and the focus ring, the contents took
     * the `role` and the handler. So the thing a keyboard could reach announced itself as nothing,
     * the thing that called itself a button couldn't be reached, and on a disabled port neither
     * fired — `PortDisabled` covers the whole glyph a layer above the contents, and a click landing
     * on the ✕ went past the handler to the container, which had none.
     *
     * The name is what the glyph draws and the DOM doesn't: detailed mode has no number in it, so
     * without this the control reads out as a bare speed, or as nothing at all. Only a clickable
     * port takes one — `aria-label` on a plain `div` is prohibited, and would trade this rule for
     * another.
     *
     * The key handler rides along with the role for the same reason: a `div` that calls itself a
     * button and takes a tab stop is one only a mouse can press without it.
     */
    <PortContainer
      aria-label={
        onClick &&
        getPortAccessibleName({
          isOutOfBandPort,
          maximumPortSpeed,
          number,
          port,
          status: disabled ? 'disabled' : status,
        })
      }
      onClick={onClick}
      onKeyDown={onClick && handleKeyDown}
      role={onClick && 'button'}
      tabIndex={onClick && 0}
      data-port={port}
      mode={mode}
      variant={portVariant}
      selected={selected}
      {...remaining}
    >
      {marksDisabled && (
        <PortDisabled>
          <PortIcon disabled={disabled} icon="cross" size={isIcon ? 8 : 12} />
        </PortDisabled>
      )}
      <PortContents disabled={marksDisabled} orientation={orientation} mode={mode}>
        {!isIcon && (
          <PortCopy mode={mode} orientation={orientation}>
            {maximumPortSpeed && showSpeed && <PortSpeedLabel speed={maximumPortSpeed} />}
            {isSimplified && showIconInSimpleMode && portIcon && (
              <PortIcon disabled={disabled} icon={portIcon} size={10} />
            )}
            {isSimplified &&
              !(showIconInSimpleMode && portIcon) &&
              (!isOutOfBandPort ? number : '~')}
          </PortCopy>
        )}
      </PortContents>
      <PortRepresentation disabled={marksDisabled}>
        <PortSVG as={NamedIcon} orientation={orientation} mode={mode} />
      </PortRepresentation>
    </PortContainer>
  );
}
